<template>
  <div id="debt">
    <DebtorTags :has-codebtor="simulation.prospect.hasCodebtor" />
    <div class="columns">
      <div class="column">
        <div v-if="showAddDebtForm" id="add-debt">
          <ValidationObserver ref="observer">
            <b-field label="Identificador de la deuda" horizontal class="field-separation">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <b-input
                  v-model="formDebt.description"
                  size="is-small"
                  placeholder="Ingrese una descripción"
                  expanded />
                <p class="help is-danger">{{ errors[0] }}</p>
              </validationprovider>
            </b-field>
            <b-field label="Indique el plazo de la deuda" horizontal class="field-separation">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <b-select
                  v-model="formDebt.term"
                  size="is-small"
                  placeholder="Selecciona el plazo"
                  data-cy="debt-term"
                  expanded>
                  <option value="LONG_TERM">Largo plazo</option>
                  <option value="SHORT_TERM">Corto plazo</option>
                </b-select>
                <p class="help is-danger">{{ errors[0] }}</p>
              </validationprovider>
            </b-field>
            <b-field label="Indique monto total de la deuda" horizontal class="field-separation">
              <BaseInput
                v-model="formDebt.totalDebtAmount"
                :rules="(!formDebt.knowsOutstandingBalance && !formDebt.knowsMonthlyPayment) || formDebt.knowsOutstandingBalance ? 'required' : ''"
                :field="{ placeholder: '0.00', type: 'number', prefix: '$', prefixPosition: 'left'}" />
            </b-field>

            <div class="field-separation">
              <b-field>
                <b-switch v-model="formDebt.knowsOutstandingBalance" size="is-small" class="mr-2" />
                <span class="is-inline-block">
                  ¿Conoce el saldo insoluto?
                </span>
              </b-field>
              <b-collapse :open="formDebt.knowsOutstandingBalance">
                <div class="columns">
                  <BaseInput
                    v-model="formDebt.outstandingBalance"
                    :rules="formDebt.knowsOutstandingBalance ? 'required' : ''"
                    :field="{ placeholder: '0.00', type: 'number', prefix: '$', prefixPosition: 'left'}"
                    class="column mt-3" />
                  <div class="column" />
                </div>
              </b-collapse>
            </div>

            <div class="field-separation">
              <b-field>
                <b-switch
                  v-model="formDebt.knowsMonthlyPayment"
                  size="is-small"
                  class="mr-2"
                  data-cy="knowsMonthlyPayment" />
                <span class="is-inline-block">
                  ¿Conoce la cuota que paga mensualmente?
                </span>
              </b-field>
              <b-collapse :open="formDebt.knowsMonthlyPayment">
                <div class="columns">
                  <BaseInput
                    v-model="formDebt.monthlyPayment"
                    :field="{
                      placeholder: '0,00',
                      type: 'number',
                      prefix: '$', prefixPosition: 'left'
                    }"
                    :rules="formDebt.knowsMonthlyPayment ? 'required' : ''"
                    class="column mt-3" />
                  <div class="column" />
                </div>
              </b-collapse>
            </div>

            <b-field
              v-if="formDebt.term === 'LONG_TERM'"
              label="Inversión"
              horizontal
              class="field-separation">
              <BaseInput
                v-model="formDebt.isInvestor"
                :field="{ type: 'switch' }" />
            </b-field>
            <b-field
              v-if="formDebt.term === 'SHORT_TERM'"
              label="Liquidación"
              horizontal
              class="field-separation">
              <BaseInput
                v-model="formDebt.isLiquidation"
                :field="{ type: 'switch' }" />
            </b-field>
          </ValidationObserver>
        </div>
        <div v-else id="debt-resume">
          <h4>Deudas ingresadas</h4>
          <table v-if="inputDebt.length > 0" class="table is-striped is-fullwidth">
            <thead>
              <tr>
                <th>Descripción</th>
                <th>Cuota Mensual</th>
                <th>Plazo</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in inputDebt" :key="i">
                <td>{{ item.description }}</td>
                <td>{{ monthlyDebtCalculate(item) }}</td>
                <td>{{ termLabels[item.term] }}</td>
                <td>
                  <b-button
                    type="is-light"
                    rounded
                    @click="removeDebt(i)">
                    <b-icon icon="trash" />
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="text-center field-separation">Sin deuda ingresada</p>
        </div>
        <b-button
          v-if="showAddDebtForm"
          type="is-primary is-light is-pulled-right mt-3"
          size="is-small"
          data-cy="btn-update-debt"
          @click="update">
          Guardar deuda
        </b-button>
        <b-button
          v-if="showAddDebtForm"
          type="is-secondary is-light is-pulled-right mt-3 mr-3"
          size="is-small"
          data-cy="btn-cancel-debt"
          @click="toggleDebt">
          Cancelar
        </b-button>
        <b-button
          v-else
          type="is-primary is-light is-pulled-right mt-3"
          size="is-small"
          data-cy="btn-show-debt"
          @click="showForm">
          <span class="mr-2">+</span> Agregar deuda
        </b-button>
      </div>
      <div class="column pl-6">
        <TableSummary
          title="Deuda total mensual"
          :icon-label="totalDebtCardSummary"
          show-icon-label
          data-cy="total-debt-card"
          :data="debtCardSummary" />
        <DocumentsVerification
          :label="label"
          :type="'debt'"
          :evaluation-id="evaluationId"
          :one-client-id="oneClientId"
          :validate-documents="validateDocuments"
          @documents-uploaded="handleDocumentsUploaded" />
      </div>
    </div>
  </div>
</template>

<script>
import TableSummary from '@/ui/components/internal/evaluations/TableSummary.vue';
import BaseInput from '@/ui/components/input/BaseInput.vue';
import {
  FinancialDataVerificatorClient
} from '@/application/services/evaluations/apis/financial-data-verificator-client';
import { formatValueToCurrency } from '@/application/services/utils/currency-formatter';
import { mapMutations, mapState } from 'vuex';
import DebtorTags from '@/ui/components/internal/evaluations/verify/DebtorTags.vue';
import DocumentsVerification
  from '@/ui/components/internal/evaluations/verify/DocumentsVerification.vue';

const defaultDebt = {
  description: '',
  monthlyPayment: null,
  term: null,
  knowsMonthlyPayment: false,
  knowsOutstandingBalance: false,
  outstandingBalance: null,
  totalDebtAmount: null,
  isLiquidation: false,
  isInvestor: false
};

export default {
  name: 'Debt',
  components: { DocumentsVerification, BaseInput, TableSummary, DebtorTags },
  props: {
    label: {
      type: String,
      required: false,
      default: 'Income'
    },
    evaluationId: {
      type: String,
      required: true
    },
    oneClientId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      termLabels: {
        LONG_TERM: 'Largo',
        SHORT_TERM: 'Corto'
      },
      debtId: null,
      showAddDebtForm: false,
      inputDebt: [],
      formDebt: { ...defaultDebt },
      labelButton: '',
      validateDocuments: false
    };
  },
  computed: {
    ...mapState('evaluation', ['debtCardSummary', 'totalDebtCardSummary', 'idNumber', 'simulation', 'debt', 'manualEvaluationId',
      'parentEvaluationId'])
  },
  async mounted() {
    if (this.manualEvaluationId && this.parentEvaluationId) {
      const isAutomatic = this.manualEvaluationId === this.parentEvaluationId;
      if (isAutomatic) {
        const shortTermMonthlyFee = this.simulation?.offerInputs.shortTermMonthlyFee ?? 0;
        const longTermMonthlyFee = this.simulation?.offerInputs.longTermMonthlyFee ?? 0;
        const existDebt = shortTermMonthlyFee !== 0 || longTermMonthlyFee !== 0;
        this.showAddDebtForm = !existDebt;
        if (existDebt) {
          const longDebt = {
            description: 'Deuda largo plazo',
            isInvestor: false,
            isLiquidation: false,
            knowsMonthlyPayment: true,
            knowsOutstandingBalance: false,
            monthlyPayment: longTermMonthlyFee,
            order: 0,
            outstandingBalance: 0,
            term: 'LONG_TERM',
            totalDebtAmount: 0
          };
          const shortDebt = {
            description: 'Deuda corto plazo',
            isInvestor: false,
            isLiquidation: false,
            knowsMonthlyPayment: true,
            knowsOutstandingBalance: false,
            monthlyPayment: shortTermMonthlyFee,
            order: longTermMonthlyFee > 0 ? 1 : 0,
            outstandingBalance: 0,
            term: 'SHORT_TERM',
            totalDebtAmount: 0
          };
          if (longTermMonthlyFee > 0) {
            this.inputDebt.push(longDebt);
          }
          if (shortTermMonthlyFee > 0) {
            this.inputDebt.push(shortDebt);
          }
          await this.evaluate();
          return;
        }
      }
    }

    const debtDebtor = this.debt.find(item => item.type === 'DEBTOR' && item.idNumber === this.idNumber);
    const { debts: inputDebtDebtor } = debtDebtor || { debts: [] };
    this.inputDebt = this.revertToOriginalFormat(inputDebtDebtor);
    this.showAddDebtForm = this.inputDebt.length === 0;
    await this.evaluate();
    this.showAddDebtForm = false;
    this.formDebt = { ...defaultDebt };
  },
  methods: {
    ...mapMutations('evaluation', ['setDebt']),
    validateNewDebt() {
      this.validateDocuments = this.inputDebt.length !== 0;
      this.$emit('new-debt-created', this.validateDocuments);
    },
    toggleDebt() {
      this.showAddDebtForm = false;
      this.labelButton = 'Agregar deuda';
      this.validateNewDebt();
    },
    monthlyDebtCalculate(item) {
      let monthlyDebtResult = 0;
      if (item.monthlyPayment && item.monthlyPayment > 0) {
        monthlyDebtResult = item.term === 'LONG_TERM' && item.isInvestor ? +item.monthlyPayment * 0.4 : +item.monthlyPayment;
      }

      let monthlyDebt = +item.totalDebtAmount * 0.015;
      if (item.term === 'SHORT_TERM') {
        monthlyDebt = +item.totalDebtAmount * 0.05;
      }

      return this.formatValue(monthlyDebtResult + monthlyDebt);
    },
    async removeDebt(index) {
      this.inputDebt.splice(index, 1);
      if (this.inputDebt.length === 0) {
        this.showAddDebtForm = true;
        this.validateNewDebt();
      }
      await this.evaluate();
    },
    async update() {
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        this.inputDebt.push(this.formDebt);

        await this.evaluate();

        this.showAddDebtForm = false;
        this.formDebt = { ...defaultDebt };
        this.validateDocuments = true;
        this.$emit('new-debt-created', this.validateDocuments);
      }
    },
    showForm() {
      this.showAddDebtForm = true;
      this.validateDocuments = true;
      this.$emit('new-debt-created', this.validateDocuments);
    },
    convertToNewFormat(debts) {
      return debts.map((item, index) => {
        return {
          order: index,
          description: item.description,
          monthlyPayment: +item.monthlyPayment,
          term: item.term,
          knowsMonthlyPayment: item.knowsMonthlyPayment,
          knowsOutstandingBalance: item.knowsOutstandingBalance,
          outstandingBalance: +item.outstandingBalance,
          totalDebtAmount: +item.totalDebtAmount,
          isLiquidation: item.isLiquidation,
          isInvestor: item.isInvestor
        };
      });
    },
    revertToOriginalFormat(debts) {
      return debts.map(item => {
        return {
          description: item.description,
          monthlyPayment: item.monthlyPayment.toString(),
          term: item.term,
          knowsMonthlyPayment: item.knowsMonthlyPayment,
          knowsOutstandingBalance: item.knowsOutstandingBalance,
          outstandingBalance: item.outstandingBalance.toString(),
          totalDebtAmount: item.totalDebtAmount.toString(),
          isLiquidation: item.isLiquidation,
          isInvestor: item.isInvestor
        };
      });
    },
    async evaluate() {
      const debts = this.convertToNewFormat(this.inputDebt);
      const financialDataVerificatorClient = new FinancialDataVerificatorClient();
      const bodyDebt = [{
        type: 'DEBTOR',
        idNumber: this.idNumber,
        debts
      }];

      const { data: calculateDebt } = await financialDataVerificatorClient.calculateDebt(bodyDebt);
      const { debtors } = calculateDebt;
      const summary = this.setSummary(debtors);

      this.setDebt({
        debt: bodyDebt,
        summary: { totalMonthlyDebt: summary.total },
        debtCardSummary: summary.detail,
        totalDebtCardSummary: summary.total
      });

      this.formDebt = { ...defaultDebt };
    },
    setSummary(debtors = []) {
      const debtor = debtors.find(item => item.type === 'DEBTOR');

      if (!debtor) {
        return {
          total: this.formatValue(0),
          detail: this.debtCardSummary
        };
      }
      const summary = this.debtCardSummary;
      summary.short.value = this.formatValue(debtor.debt.monthlyShortDebt);
      summary.long.value = this.formatValue(debtor.debt.monthlyLongDebt);
      return {
        total: this.formatValue(debtor.debt.monthlyDebt),
        detail: summary
      };
    },
    formatValue(value) {
      return formatValueToCurrency(value, this.$i18n.locale);
    },
    handleDocumentsUploaded(uploadedDocumentsLength) {
      this.$emit('debt-documents-uploaded', uploadedDocumentsLength);
    }
  }
};
</script>
<style scoped>
.income-tag {
  width: 140px;
}

.field-separation {
  padding: 1rem 0;
}
.text-center {
  text-align: center;
}

.text-left{
  text-align: left;
}

@media screen and (min-width: 992px) {
  .field {
    margin-bottom: 0 !important;
  }
}
</style>
<style>
@media screen and (min-width: 992px) {
  #debt .is-horizontal .field-label {
    flex-grow: 6;
  }
}
</style>
